import type React from "react";
import { useState, useCallback } from "react";
import { X } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@planta-v2/pds-ui/dialog";
import { WhatsAppTemplateSafeTextarea } from "@/shared/presentation/components/WhatsAppTemplateSafeTextarea";
import { i18n } from "@frontend/i18n";

const MAX_CHARS = 400;
const SHOW_COUNTER_AT = 350;

export interface RequestIntroModalProps {
  readonly isOpen: boolean;

  readonly onClose: () => void;

  readonly onSubmit: (message: string) => void;

  readonly isSubmitting?: boolean | undefined;

  readonly targetName?: string | undefined;
}

export function RequestIntroModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  targetName,
}: RequestIntroModalProps): React.JSX.Element {
  const [message, setMessage] = useState("");

  const charCount = message.length;
  const isOverLimit = charCount > MAX_CHARS;
  const showCounter = charCount >= SHOW_COUNTER_AT;

  const handleMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (!isOverLimit && !isSubmitting) {
      onSubmit(message);
      // Message is reset in handleOpenChange when modal closes
      // This preserves the message if onSubmit fails
    }
  }, [message, isOverLimit, isSubmitting, onSubmit]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
        setMessage("");
      }
    },
    [onClose],
  );

  const placeholder =
    targetName !== undefined && targetName !== ""
      ? i18n.t("user.directory.profile.requestIntroModal.placeholder", {
          name: targetName,
        })
      : i18n.t("user.directory.profile.requestIntroModal.placeholderGeneric");

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* Scrim is 80% black rather than the default 50% so 20% of the content
          behind stays visible; width 357px matches the Figma spec. */}
      <DialogContent
        data-testid="request-intro-modal"
        overlayClassName="bg-black/80"
        className="flex w-[357px] flex-col p-4 sm:max-w-none"
        showCloseButton={false}
      >
        {/* Header with title and close button */}
        <div className="flex items-center justify-between gap-2">
          <DialogTitle className="text-xl font-medium">
            {i18n.t("user.directory.profile.requestIntroModal.title")}
          </DialogTitle>
          {/* Visually hidden description for screen readers */}
          <DialogDescription className="sr-only">
            {i18n.t(
              "user.directory.profile.requestIntroModal.placeholderGeneric",
            )}
          </DialogDescription>
          <button
            type="button"
            onClick={onClose}
            data-testid="request-intro-close-button"
            className="text-foreground opacity-50 hover:opacity-100 transition-opacity"
            aria-label={i18n.t(
              "user.directory.profile.requestIntroModal.close",
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Message textarea */}
        <div className="flex flex-col gap-0">
          <div className="relative">
            <WhatsAppTemplateSafeTextarea
              value={message}
              onChange={handleMessageChange}
              placeholder={placeholder}
              data-testid="request-intro-message-input"
              className="min-h-[96px] resize-none"
              disabled={isSubmitting}
              aria-describedby="request-intro-message-hint"
            />
          </div>
          {/* Hint clarifying the WhatsApp-template input restriction */}
          <p
            id="request-intro-message-hint"
            data-testid="request-intro-message-hint"
            className="text-pds-2xs text-muted-foreground mt-1"
          >
            {i18n.t("user.directory.profile.requestIntroModal.messageHint")}
          </p>
          {/* Character counter - only show at 350+ chars */}
          {showCounter && (
            <div className="flex justify-end mt-1">
              <span
                data-testid="request-intro-char-counter"
                className={`text-pds-2xs ${
                  isOverLimit ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {i18n.t("user.directory.profile.requestIntroModal.charCount", {
                  current: charCount,
                  max: MAX_CHARS,
                })}
              </span>
            </div>
          )}
        </div>

        {/* Send button - uses inverted colors (dark on light, light on dark) */}
        <Button
          onClick={handleSubmit}
          disabled={isOverLimit || isSubmitting}
          data-testid="request-intro-send-button"
          className="w-full h-10 rounded-lg font-semibold text-sm bg-foreground text-background hover:bg-foreground/90 disabled:opacity-30"
        >
          {isSubmitting
            ? i18n.t("user.directory.profile.requestIntroModal.sending")
            : i18n.t("user.directory.profile.requestIntroModal.send")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
